import { createFileRoute } from "@tanstack/react-router"
import { Download, RefreshCw, Sparkles } from "lucide-react"

import { Button } from "@/components/button"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { BossProfileCard } from "@/components/talent-config/boss-profile-card"
import { HierarchyEditor } from "@/components/talent-config/hierarchy-editor"
import { RosterCard } from "@/components/talent-config/roster-card"
import { TrackSection } from "@/components/talent-config/track-section"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Card } from "@/components/ui/card"
import { FormPageSkeleton } from "@/components/ui/page-skeletons"
import {
  useConfigQuery,
  useDeleteBossMutation,
  useDeleteTalentMutation,
  useResetSeason,
  useSelectedBoss,
  useTalentConfigExportMutation,
  useUpdateDraftOnBossChange,
} from "@/hooks/talent-config"
import {
  createTalentConfigUiStore,
  type TalentConfigUiStore,
} from "@/lib/stores/talent-config-ui-store"
import {
  useDeleteTarget,
  setDeleteTarget,
} from "@/lib/stores/talent-config-ui-store"
import {
  type TalentCategory,
  type TalentConfigSnapshot,
} from "@/lib/talent-config-api"

export const Route = createFileRoute("/_authed/_admin/talent-config")({
  beforeLoad: () => ({
    talentConfigStore: createTalentConfigUiStore(),
  }),
  component: TalentConfigRoute,
})

const CATEGORY_LABELS: Record<TalentCategory, string> = {
  offensive: "Offensive",
  defensive: "Defensive",
  strategic: "Strategic",
}

const TRACK_ORDER: TalentCategory[] = ["offensive", "defensive", "strategic"]

const getMetrics = (snapshot: TalentConfigSnapshot | undefined) => {
  if (!snapshot) {
    return {
      bosses: 0,
      talents: 0,
      seasons: 0,
    }
  }

  return {
    bosses: snapshot.bosses.length,
    talents: snapshot.talents.length,
    seasons: snapshot.seasons.length,
  }
}

const TalentConfigPageHeader: React.FC<{
  snapshot: TalentConfigSnapshot | undefined
  store: TalentConfigUiStore
  isFetching: boolean
  onRefresh: () => void
}> = ({ snapshot, store, isFetching, onRefresh }) => {
  const metrics = getMetrics(snapshot)
  const exportMutation = useTalentConfigExportMutation(store)

  return (
    <Card className="page-intro border-primary/10">
      <PageIntroSplitHeader
        kicker={
          <SectionKicker icon={<Sparkles />}>Talent Control</SectionKicker>
        }
        title="Curate seasonal boss talent data without leaving the admin deck."
        titleClassName="max-w-4xl text-5xl leading-none font-semibold md:text-6xl"
        description="Review every boss by season, tune display metadata, and keep each talent requirement and reward polished for the dashboard and tracking flows that sit on top of it."
        descriptionClassName="max-w-3xl text-base leading-7"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <RefreshButton isFetching={isFetching} onClick={onRefresh} />
            <Button
              type="button"
              variant="outline"
              disabled={exportMutation.isPending}
              onClick={() => {
                void exportMutation.mutateAsync()
              }}
            >
              <Download className="size-4" />
              {exportMutation.isPending
                ? "Exporting JSON..."
                : "Export DB to JSON"}
            </Button>
          </div>
        }
        metrics={
          <>
            <MetricTile
              label="Bosses"
              value={metrics.bosses.toString()}
              detail="Stored in the config cache and backed by DB rows."
            />
            <MetricTile
              label="Talents"
              value={metrics.talents.toString()}
              detail="Requirements and rewards currently available to users."
            />
            <MetricTile
              label="Seasons"
              value={metrics.seasons.toString()}
              detail="Switch quickly between live and future seasonal sets."
            />
          </>
        }
      />
    </Card>
  )
}

function TalentConfigRoute() {
  const { talentConfigStore } = Route.useRouteContext()
  const configQuery = useConfigQuery()

  const snapshot = configQuery.data
  const deleteTarget = useDeleteTarget(talentConfigStore)

  useResetSeason(talentConfigStore, snapshot)

  const selectedBoss = useSelectedBoss(talentConfigStore, snapshot)

  useUpdateDraftOnBossChange(talentConfigStore, selectedBoss)

  const deleteBossMutation = useDeleteBossMutation(talentConfigStore)

  const deleteTalentMutation = useDeleteTalentMutation(talentConfigStore)

  const handleDeleteConfirm = () => {
    if (!deleteTarget) {
      return
    }

    if (deleteTarget.kind === "boss") {
      deleteBossMutation.mutate(deleteTarget.id)
    } else {
      deleteTalentMutation.mutate(deleteTarget.id)
    }

    setDeleteTarget(talentConfigStore, null)
  }

  if (configQuery.isLoading) {
    return <FormPageSkeleton fieldCount={6} />
  }

  if (configQuery.isError) {
    return (
      <div className="grid gap-4">
        <StatusAlert tone="danger">
          {configQuery.error instanceof Error
            ? configQuery.error.message
            : "Failed to load talent config"}
        </StatusAlert>
        <Button
          type="button"
          variant="outline"
          onClick={() => void configQuery.refetch()}
        >
          <RefreshCw className="size-4" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      <TalentConfigPageHeader
        snapshot={snapshot}
        store={talentConfigStore}
        isFetching={configQuery.isFetching}
        onRefresh={() => void configQuery.refetch()}
      />

      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <RosterCard snapshot={snapshot} />

        <div className="grid gap-4">
          {selectedBoss ? (
            <>
              <BossProfileCard boss={selectedBoss} snapshot={snapshot} />
              <HierarchyEditor boss={selectedBoss} />
              {TRACK_ORDER.map((category) => (
                <TrackSection
                  key={category}
                  boss={selectedBoss}
                  category={category}
                />
              ))}
            </>
          ) : null}
        </div>
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(talentConfigStore, null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "boss"
                ? `Delete ${deleteTarget.name}?`
                : deleteTarget?.kind === "talent"
                  ? `Delete ${CATEGORY_LABELS[deleteTarget.category!]} tier ${deleteTarget.tier}?`
                  : "Delete item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "boss"
                ? "This removes the selected boss and all of its talents. This action cannot be undone."
                : "This removes the selected talent slot. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={
                deleteBossMutation.isPending || deleteTalentMutation.isPending
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={
                deleteBossMutation.isPending || deleteTalentMutation.isPending
              }
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
